import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Badge from '@mui/material/Badge';
import Container from '@mui/material/Container';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import SettingsIcon from '@mui/icons-material/Settings';
import { useConfig } from '../../context';
import { Title } from '../../styles';
import { OptimisedToolbarProps } from './table.types';
import { ActionButton } from '..';

export const OptimisedToolbar: React.FC<OptimisedToolbarProps> = React.memo(
    ({
        title,
        numOfItems,
        searchOptions,
        searchOptionHandler,
        searchHandler,
        Settings,
        Actions
    }) => {
        const { tableSettings, dispatch } = useConfig();
        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(
            null
        );
        const open = Boolean(anchorEl);
        const openSettingsHandler = React.useCallback(
            (event: React.MouseEvent<HTMLElement>) =>
                setAnchorEl(event.currentTarget),
            []
        );
        const closeSettingsHandler = React.useCallback(
            () => setAnchorEl(null),
            []
        );
        const toggleDensity = React.useCallback(
            () => dispatch({ type: 'TOGGLE_TABLE_SETTINGS_DENSITY' }),
            [dispatch]
        );
        const toggleBadge = React.useCallback(
            () => dispatch({ type: 'TOGGLE_TABLE_SETTINGS_BADGE' }),
            [dispatch]
        );
        const togglePagination = React.useCallback(
            () => dispatch({ type: 'TOGGLE_TABLE_SETTINGS_PAGINATION' }),
            [dispatch]
        );
        const toggleSearch = React.useCallback(
            () => dispatch({ type: 'TOGGLE_TABLE_SETTINGS_SEARCH' }),
            [dispatch]
        );
        return (
            <Stack
                direction='row'
                justifyContent='space-between'
                padding='1rem'
                sx={{ background: '#e0e0e0' }}
            >
                {title ? (
                    tableSettings.disableBadge ? (
                        <Title size='medium' fontWeight={500}>
                            {title}
                        </Title>
                    ) : (
                        <Badge
                            badgeContent={numOfItems}
                            color='primary'
                            max={999}
                        >
                            <Title size='medium' fontWeight={500}>
                                {title}
                            </Title>
                        </Badge>
                    )
                ) : (
                    <Container />
                )}
                <Stack flexDirection='row'>
                    {!tableSettings.disableSearch && (
                        <Autocomplete
                            id='search-clients'
                            options={searchOptions ? searchOptions : []}
                            onChange={searchOptionHandler}
                            size='small'
                            sx={{ width: '10rem' }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label='Search'
                                    name='filter'
                                    onChange={searchHandler}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                />
                            )}
                        />
                    )}
                    {Actions}
                    <ActionButton
                        Icon={<SettingsIcon />}
                        onClick={openSettingsHandler}
                        sx={{ ml: '1rem' }}
                    />
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={closeSettingsHandler}
                        onClick={closeSettingsHandler}
                        transformOrigin={{
                            horizontal: 'right',
                            vertical: 'top'
                        }}
                        anchorOrigin={{
                            horizontal: 'right',
                            vertical: 'bottom'
                        }}
                    >
                        <MenuItem onClick={toggleDensity}>
                            {tableSettings.dense
                                ? 'Comfortable View'
                                : 'Dense View'}
                        </MenuItem>
                        <MenuItem onClick={toggleBadge}>
                            {tableSettings.disableBadge
                                ? 'Enable Badge'
                                : 'Disable Badge'}
                        </MenuItem>
                        <MenuItem onClick={togglePagination}>
                            {tableSettings.disablePagination
                                ? 'Enable Pagination'
                                : 'Disable Pagination'}
                        </MenuItem>
                        <MenuItem onClick={toggleSearch}>
                            {tableSettings.disableSearch
                                ? 'Enable Search'
                                : 'Disable Search'}
                        </MenuItem>
                        {Settings}
                    </Menu>
                </Stack>
            </Stack>
        );
    }
);
